import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import {
  useGetByTenantClientQuery,
  useGetByTenantCounterpartyAllQuery,
  usePostByTenantNoteAndNoteIdChangeBusinessunitNewBusinessUnitIdMutation,
  usePostByTenantNoteAndNoteIdChangeCounterpartyNewCounterpartyIdMutation
} from '@/api/rtk-api'
import {
  Alert,
  ConfirmUsernameField,
  FormError,
  LabelValueRow,
  Loading,
  OperationButton,
  RadioFieldSet,
  SelectField
} from '@/components/core-v2'
import { PATH, TENANT } from '@/const'

import classes from './transfer-tab.module.scss'

interface FormFields {
  businessUnitId: string
  counterpartyId: string
  type: 'business-unit' | 'counterparty'
}
interface Props {
  businessUnitName: string
  counterpartyId: string
  isClosed: boolean
  isLocked: boolean
  noteId: string
}
export const TransferTab = ({
  businessUnitName,
  counterpartyId,
  isClosed,
  isLocked,
  noteId
}: Props) => {
  const [changeCounterparty, changeCounterpartyStatus] =
    usePostByTenantNoteAndNoteIdChangeCounterpartyNewCounterpartyIdMutation()
  const [changeBusinessUnit, changeBusinessUnitStatus] =
    usePostByTenantNoteAndNoteIdChangeBusinessunitNewBusinessUnitIdMutation()
  const navigate = useNavigate()
  const form = useForm<FormFields>({
    defaultValues: {
      type: 'business-unit'
    }
  })

  const typeValue = form.watch('type')

  const counterpartyQuery = useGetByTenantCounterpartyAllQuery({
    tenant: TENANT
  })

  const clientQuery = useGetByTenantClientQuery({ tenant: TENANT })

  const isLoading = counterpartyQuery.isLoading || clientQuery.isLoading

  useEffect(() => {
    // If the type changes, reset all errors etc.
    changeBusinessUnitStatus.reset()
    changeCounterpartyStatus.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeValue])

  useEffect(() => {
    if (changeBusinessUnitStatus.isSuccess || changeCounterpartyStatus.isSuccess) {
      toast.success('Transfer submitted')
      navigate(PATH.reportsNotes)
    }
  }, [changeBusinessUnitStatus.isSuccess, changeCounterpartyStatus.isSuccess, navigate])

  if (isLoading) return <Loading />

  if (isClosed)
    return (
      <Alert type="warning">
        This action is not available because all trades within the note have been closed.
      </Alert>
    )

  if (isLocked)
    return (
      <Alert type="warning">
        The counterparty and business unit for this note cannot be changed at this time because the
        note contains inflight operations.
      </Alert>
    )

  const counterpartyOptions = counterpartyQuery.data.reduce((options, cp) => {
    if (cp.counterpartyId !== counterpartyId)
      options.push({
        value: cp.counterpartyId,
        label: `${cp.counterpartyName} (${cp.counterpartyId})`
      })
    return options
  }, [])

  const businessUnitOptions = clientQuery.data.reduce((options, client) => {
    client.businessUnits.forEach(bu => {
      if (bu.businessUnitName !== businessUnitName)
        options.push({ value: bu.businessUnitId, label: bu.businessUnitName })
    })

    return options
  }, [])

  const onSubmit = (formValues: FormFields) => {
    if (formValues.type === 'business-unit') {
      changeBusinessUnit({ tenant: TENANT, noteId, newBusinessUnitId: formValues.businessUnitId })
    }
    if (formValues.type === 'counterparty') {
      changeCounterparty({ tenant: TENANT, noteId, newCounterpartyId: formValues.counterpartyId })
    }
  }

  let formServerErrors: string[] = []
  if (changeCounterpartyStatus.isError && 'data' in changeCounterpartyStatus.error) {
    formServerErrors = [
      ...formServerErrors,
      ...((changeCounterpartyStatus?.error?.data || []) as string[])
    ]
  }
  if (changeBusinessUnitStatus.isError && 'data' in changeBusinessUnitStatus.error)
    formServerErrors = [
      ...formServerErrors,
      ...((changeBusinessUnitStatus.error.data || []) as string[])
    ]
  const isServerError = changeCounterpartyStatus.isError || changeBusinessUnitStatus.isError
  const isFormLoading = changeCounterpartyStatus.isLoading || changeBusinessUnitStatus.isLoading

  const counterparty = counterpartyQuery.data.find(cp => cp.counterpartyId === counterpartyId)

  return (
    <div className={classes.root}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <LabelValueRow label="Current business unit" value={businessUnitName} />
          <LabelValueRow
            showBottomBorder
            className="mb-10"
            label="Current counterparty"
            value={`${counterparty.counterpartyName} (${counterpartyId})`}
          />
          <RadioFieldSet
            className="mb-8"
            legend="Choose an option"
            name="type"
            options={[
              { value: 'business-unit', label: 'Transfer to another business unit' },
              { value: 'counterparty', label: 'Transfer to another Counterparty' }
            ]}
          />
          {typeValue === 'business-unit' && (
            <SelectField
              className="mb-8"
              id="businessUnitId"
              label="Choose a business unit"
              options={businessUnitOptions}
              variant="column"
            />
          )}
          {typeValue === 'counterparty' && (
            <SelectField
              className="mb-8"
              id="counterpartyId"
              label="Choose a counterparty ID"
              options={counterpartyOptions}
              variant="column"
            />
          )}
          <ConfirmUsernameField
            className="mb-4"
            label="To submit the transfer, enter your username below and click on the button"
          />
          <FormError collapseClassName="my-4" isShowing={isServerError}>
            {formServerErrors &&
              formServerErrors.map(error => (
                <p key={error} className="mb-0">
                  {error}
                </p>
              ))}
          </FormError>
          <OperationButton isLoading={isFormLoading} operationType="progress">
            Submit transfer
          </OperationButton>
        </form>
      </FormProvider>
    </div>
  )
}
